import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Target, Plus, RefreshCw, TrendingUp, Calendar, Check, X, Copy, Trash2, Edit2, ChevronRight, ChevronDown, ArrowRight, Percent, Hash, Eye, EyeOff, GripVertical, Archive, Play, Pause, Filter, Clock, FileDown, BarChart3, Columns, ArrowLeftRight, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { safePrintHTML } from '@/utils/safePrint';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
  LineChart, Line
} from 'recharts';

const API_BASE = '/cutting-simulator';

// Format date to DD/MM
const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length >= 3) return `${parts[2]}/${parts[1]}`;
  return dateStr;
};

// Status definitions with colors and labels
const STATUS_CONFIG = {
  ativa: { label: 'Ativa', color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-500/10', border: 'border-emerald-400' },
  iniciada: { label: 'Iniciada', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-500/10', border: 'border-blue-400' },
  pendente: { label: 'Pendente', color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-500/10', border: 'border-amber-400' },
  arquivada: { label: 'Arquivada', color: 'bg-slate-400', textColor: 'text-slate-500', bgLight: 'bg-slate-400/10', border: 'border-slate-300' },
  concluida: { label: 'Concluída', color: 'bg-violet-500', textColor: 'text-violet-600', bgLight: 'bg-violet-500/10', border: 'border-violet-400' },
};

// Format time as decimal minutes (e.g., 3.5 min)
const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return '0';
  return minutes.toFixed(1).replace('.', ',');
};

// Format time in hours as HH:MM
const formatHoursAsHHMM = (decimalHours) => {
  if (decimalHours === null || decimalHours === undefined || isNaN(decimalHours)) return '00:00';
  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Indicator labels in Portuguese
const INDICATOR_LABELS = {
  tempo_morto_entre: 'Tempo Morto Entre Marcadas',
  tempo_morto_intra: 'Tempo Morto Intra Marcadas',
  maquina_trabalhar: 'Máquina a Trabalhar',
  num_marcadas: 'N.º de Marcadas',
  perimetro: 'Perímetro',
  velocidade: 'Velocidade'
};

const INDICATOR_UNITS = {
  tempo_morto_entre: 'h',
  tempo_morto_intra: 'h',
  maquina_trabalhar: 'h',
  num_marcadas: '',
  perimetro: 'm',
  velocidade: 'm/min'
};

// Check if indicator is time-based
const isTimeIndicator = (indicator) => ['tempo_morto_entre', 'tempo_morto_intra', 'maquina_trabalhar'].includes(indicator);

// Check if lower is better for this indicator (dead time)
const isInverseIndicator = (indicator) => ['tempo_morto_entre', 'tempo_morto_intra'].includes(indicator);


// Edit Objective Modal - allows changing targets with simulation preview
function EditObjectiveModal({ objective, t, onClose, onSave, formatNumber, formatHoursAsHHMM }) {
  const bm = objective.base_metrics || {};
  const totalMin = (bm.total_time_hours || 1) * 60;
  const baseCuttingMin = bm.maquina_trabalhar_min || 0;
  const baseStoppedMin = bm.tempo_morto_intra_min || 0;
  const baseEntreMin = bm.tempo_morto_entre_min || 0;
  const baseNumCuts = bm.num_marcadas || 1;
  const baseAvgEntre = baseNumCuts > 0 ? baseEntreMin / baseNumCuts : 0; // média em min

  // Parse saved targets from array format
  const savedTargets = {};
  if (Array.isArray(objective.targets)) {
    objective.targets.forEach(t => { savedTargets[t.indicator] = t.value; });
  }

  // Targets: intra as %, entre as minutes per cut
  const [targetIntraPct, setTargetIntraPct] = useState(
    savedTargets.tempo_morto_intra ?? bm.tempo_morto_intra_pct ?? 0
  );
  
  // For entre: if saved as pct_of_day, convert back to min/cut
  const savedEntrePct = savedTargets.tempo_morto_entre;
  const entreInitialMin = savedEntrePct != null 
    ? (savedEntrePct / 100 * totalMin / (baseNumCuts || 1))
    : (Math.round(baseAvgEntre * 10) / 10 || 2);
  const [targetEntreMin, setTargetEntreMin] = useState(entreInitialMin);

  // Simulation calculations
  const simIntraMin = targetIntraPct / 100 * totalMin;
  const simEntreMinTotal = targetEntreMin * baseNumCuts;
  const simCuttingMin = Math.max(0, totalMin - simIntraMin - simEntreMinTotal);
  
  // Percentages
  const simIntraPctOfDay = totalMin > 0 ? (simIntraMin / totalMin * 100) : 0;
  const simEntrePctOfDay = totalMin > 0 ? (simEntreMinTotal / totalMin * 100) : 0;
  const simCuttingPctOfDay = totalMin > 0 ? (simCuttingMin / totalMin * 100) : 0;
  const simIntraPctOfCut = simCuttingMin > 0 ? (simIntraMin / simCuttingMin * 100) : 0;
  const baseIntraPctOfCut = baseCuttingMin > 0 ? (baseStoppedMin / baseCuttingMin * 100) : 0;

  // Savings: poupança = redução nos tempos mortos
  const savedIntra = baseStoppedMin - simIntraMin;
  const savedEntre = baseEntreMin - simEntreMinTotal;
  const totalSaved = savedIntra + savedEntre; // tempo libertado dos mortos
  const extraCutting = simCuttingMin - baseCuttingMin; // desse tempo, quanto vai para corte

  // Efficiency: Corte / (Corte + Intra) = tempo a cortar vs tempo de execução
  const currentEff = (baseCuttingMin + baseStoppedMin) > 0 ? (baseCuttingMin / (baseCuttingMin + baseStoppedMin) * 100) : 0;
  const simEff = (simCuttingMin + simIntraMin) > 0 ? (simCuttingMin / (simCuttingMin + simIntraMin) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${t.card} rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto`}>
        <div className="p-6 border-b border-gray-200 dark:border-zinc-700">
          <h2 className={`text-xl font-bold ${t.text}`}>Editar Objetivo: {objective.name}</h2>
          <p className={`text-sm ${t.textMut}`}>Ajuste os parâmetros alvo — a simulação atualiza automaticamente</p>
        </div>

        <div className="p-6 space-y-8">
          {/* Slider 1: Intra Marcadas (%) */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <div>
                <span className={`text-sm font-bold ${t.text}`}>Tempo Intra Marcadas</span>
                <span className={`block text-xs ${t.textMut}`}>% do dia parada dentro das marcadas</span>
              </div>
              <div className="text-right">
                <span className={`text-xs ${t.textMut}`}>Base: {formatNumber(bm.tempo_morto_intra_pct, 1)}%</span>
                <span className={`block text-2xl font-black text-red-500`}>{formatNumber(targetIntraPct, 1)}%</span>
              </div>
            </div>
            <div className="relative h-4 rounded-full overflow-hidden" style={{ background: t.key === 'dark' ? '#27272a' : '#e2e8f0' }}>
              <div className="absolute inset-0 h-full rounded-full bg-gradient-to-r from-red-500 to-red-400 transition-all" style={{ width: `${Math.min(100, targetIntraPct / 80 * 100)}%` }} />
              <input
                type="range" min="0" max="80" step="0.5"
                value={targetIntraPct}
                onChange={(e) => setTargetIntraPct(parseFloat(e.target.value))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
            <div className={`flex justify-between text-xs ${t.textMut} mt-1`}>
              <span>0% (ideal)</span>
              <span>80%</span>
            </div>
          </div>

          {/* Slider 2: Entre Marcadas (minutos por corte) */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <div>
                <span className={`text-sm font-bold ${t.text}`}>Tempo Entre Marcadas</span>
                <span className={`block text-xs ${t.textMut}`}>Média de tempo entre cada corte</span>
              </div>
              <div className="text-right">
                <span className={`text-xs ${t.textMut}`}>Base: {formatNumber(baseAvgEntre, 1)} min/corte</span>
                <span className={`block text-2xl font-black text-amber-500`}>{formatNumber(targetEntreMin, 1)} min</span>
              </div>
            </div>
            <div className="relative h-4 rounded-full overflow-hidden" style={{ background: t.key === 'dark' ? '#27272a' : '#e2e8f0' }}>
              <div className="absolute inset-0 h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all" style={{ width: `${Math.min(100, targetEntreMin / 10 * 100)}%` }} />
              <input
                type="range" min="0.1" max="10" step="0.1"
                value={targetEntreMin}
                onChange={(e) => setTargetEntreMin(parseFloat(e.target.value))}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>
            <div className={`flex justify-between text-xs ${t.textMut} mt-1`}>
              <span>0:06 (rápido)</span>
              <span>10:00 (lento)</span>
            </div>
          </div>

          {/* Auto-calculated: Máquina a Trabalhar */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <div>
                <span className={`text-sm font-bold ${t.text}`}>Máquina a Trabalhar</span>
                <span className={`block text-xs ${t.textMut}`}>Auto-calculado (restante do dia)</span>
              </div>
              <div className="text-right">
                <span className={`text-xs ${t.textMut}`}>Base: {formatNumber(bm.maquina_trabalhar_pct, 1)}%</span>
                <span className={`block text-2xl font-black text-emerald-500`}>{formatNumber(simCuttingPctOfDay, 1)}%</span>
              </div>
            </div>
            <div className="relative h-4 rounded-full overflow-hidden" style={{ background: t.key === 'dark' ? '#27272a' : '#e2e8f0' }}>
              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${Math.min(100, simCuttingPctOfDay)}%` }} />
            </div>
          </div>

          {/* Simulation Table */}
          <div className={`rounded-xl overflow-hidden border-2 border-violet-500/30`}>
            <div className="p-4 bg-violet-500/10">
              <h3 className={`text-sm font-bold ${t.text}`}>Simulação de Ganhos</h3>
            </div>
            <table className={`w-full text-sm ${t.text}`}>
              <thead>
                <tr className={`${t.inner} text-xs uppercase`}>
                  <th className="text-left p-3">Indicador</th>
                  <th className="text-center p-3">Actual</th>
                  <th className="text-center p-3 text-violet-500">Simulado</th>
                  <th className="text-center p-3">Diferença</th>
                </tr>
              </thead>
              <tbody>
                <tr className={`border-t ${t.divider}`}>
                  <td className="p-3">% do Dia a Cortar</td>
                  <td className="p-3 text-center">{formatNumber(bm.maquina_trabalhar_pct, 1)}%</td>
                  <td className="p-3 text-center text-violet-500 font-bold">{formatNumber(simCuttingPctOfDay, 1)}%</td>
                  <td className="p-3 text-center">
                    <span className={simCuttingPctOfDay > (bm.maquina_trabalhar_pct || 0) ? 'text-emerald-500' : 'text-red-400'}>
                      {(simCuttingPctOfDay - (bm.maquina_trabalhar_pct || 0)) > 0 ? '+' : ''}{formatNumber(simCuttingPctOfDay - (bm.maquina_trabalhar_pct || 0), 1)}pp
                    </span>
                  </td>
                </tr>
                <tr className={`border-t ${t.divider}`}>
                  <td className="p-3">% Intra/Corte</td>
                  <td className="p-3 text-center">{formatNumber(baseIntraPctOfCut, 0)}%</td>
                  <td className="p-3 text-center text-violet-500 font-bold">{formatNumber(simIntraPctOfCut, 0)}%</td>
                  <td className="p-3 text-center">
                    <span className={simIntraPctOfCut < baseIntraPctOfCut ? 'text-emerald-500' : 'text-red-400'}>
                      {(simIntraPctOfCut - baseIntraPctOfCut) > 0 ? '+' : ''}{formatNumber(simIntraPctOfCut - baseIntraPctOfCut, 0)}pp
                    </span>
                  </td>
                </tr>
                <tr className={`border-t ${t.divider}`}>
                  <td className="p-3">
                    <div>Eficiência</div>
                    <div className={`text-[10px] ${t.textMut}`}>Corte / (Corte + Intra) — tempo a cortar dentro do tempo de execução</div>
                  </td>
                  <td className="p-3 text-center">{formatNumber(currentEff, 1)}%</td>
                  <td className="p-3 text-center text-violet-500 font-bold">{formatNumber(simEff, 1)}%</td>
                  <td className="p-3 text-center">
                    <span className={simEff > currentEff ? 'text-emerald-500' : 'text-red-400'}>
                      {(simEff - currentEff) > 0 ? '+' : ''}{formatNumber(simEff - currentEff, 1)}pp
                    </span>
                  </td>
                </tr>
                <tr className={`border-t ${t.divider}`}>
                  <td className="p-3">
                    <div>Poupança Intra</div>
                    <div className={`text-[10px] ${t.textMut}`}>Redução no tempo parado</div>
                  </td>
                  <td className="p-3 text-center">{formatHoursAsHHMM(baseStoppedMin / 60)}</td>
                  <td className="p-3 text-center text-violet-500 font-bold">{formatHoursAsHHMM(simIntraMin / 60)}</td>
                  <td className="p-3 text-center">
                    <span className={savedIntra > 0 ? 'text-emerald-500 font-bold' : 'text-red-400'}>
                      {savedIntra > 0 ? '-' : '+'}{formatHoursAsHHMM(Math.abs(savedIntra) / 60)}
                    </span>
                  </td>
                </tr>
                <tr className={`border-t ${t.divider}`}>
                  <td className="p-3">
                    <div>Poupança Entre</div>
                    <div className={`text-[10px] ${t.textMut}`}>Redução no tempo entre marcadas</div>
                  </td>
                  <td className="p-3 text-center">{formatHoursAsHHMM(baseEntreMin / 60)}</td>
                  <td className="p-3 text-center text-violet-500 font-bold">{formatHoursAsHHMM(simEntreMinTotal / 60)}</td>
                  <td className="p-3 text-center">
                    <span className={savedEntre > 0 ? 'text-emerald-500 font-bold' : 'text-red-400'}>
                      {savedEntre > 0 ? '-' : '+'}{formatHoursAsHHMM(Math.abs(savedEntre) / 60)}
                    </span>
                  </td>
                </tr>
                <tr className={`border-t-2 ${t.divider} font-bold`}>
                  <td className="p-3 text-sky-500">Tempo Extra Corte</td>
                  <td className="p-3 text-center">{formatHoursAsHHMM(baseCuttingMin / 60)}</td>
                  <td className="p-3 text-center text-violet-500">{formatHoursAsHHMM(simCuttingMin / 60)}</td>
                  <td className="p-3 text-center">
                    <span className={extraCutting > 0 ? 'text-emerald-500 font-bold' : 'text-red-400'}>
                      {extraCutting > 0 ? '+' : ''}{formatHoursAsHHMM(extraCutting / 60)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-zinc-700 flex justify-end gap-3">
          <Button onClick={onClose} variant="outline">Cancelar</Button>
          <Button onClick={() => onSave([
            { indicator: 'tempo_morto_intra', type: 'pct_of_day', value: targetIntraPct },
            { indicator: 'tempo_morto_entre', type: 'pct_of_day', value: simEntrePctOfDay },
            { indicator: 'maquina_trabalhar', type: 'pct_of_day', value: simCuttingPctOfDay },
          ])} className="bg-violet-500 hover:bg-violet-600 text-white">
            Guardar Objetivo
          </Button>
        </div>
      </div>
    </div>
  );
}

// Memoized Impactful Report View Component with Chart and PDF Export
const ObjectiveReportView = React.memo(function ObjectiveReportView({ 
  objective, showTotalHours, setShowTotalHours, 
  hiddenIndicators, toggleIndicator, onEdit,
  formatNumber, formatMinutes, formatHoursAsHHMM 
}) {
  const [showObjectiveColumn, setShowObjectiveColumn] = useState(true);
  const [showChart, setShowChart] = useState(false); // Default to false for performance
  const reportRef = useRef(null);
  
  // Calculate days info - memoized
  const baseDays = useMemo(() => objective.base_metrics?.days_count || 0, [objective.base_metrics?.days_count]);
  const compDays = useMemo(() => objective.achieved_metrics?.days_count || 0, [objective.achieved_metrics?.days_count]);
  
  // Prepare chart data with full names for radar chart - memoized
  const chartData = useMemo(() => ['tempo_morto_entre', 'tempo_morto_intra', 'maquina_trabalhar']
    .filter(ind => !hiddenIndicators?.includes(ind))
    .map(indicator => {
      const data = objective.report[indicator];
      const baseNumCuts = objective.base_metrics?.num_marcadas || 1;
      const achievedNumCuts = objective.achieved_metrics?.num_marcadas || 1;
      const baseMinutes = data.base.time_hours * 60;
      const achievedMinutes = data.achieved.time_hours * 60;
      
      // Full readable names for radar chart
      const fullNames = {
        'tempo_morto_entre': 'Tempo Entre Cortes',
        'tempo_morto_intra': 'Tempo Intra Corte',
        'maquina_trabalhar': 'Máquina a Trabalhar'
      };
      
      return {
        name: INDICATOR_LABELS[indicator].replace('Tempo Morto ', 'T.M. ').replace(' Marcadas', ''),
        fullName: fullNames[indicator] || INDICATOR_LABELS[indicator],
        base: showTotalHours === 'total' ? data.base.time_hours : baseMinutes / baseNumCuts,
        alcancado: showTotalHours === 'total' ? data.achieved.time_hours : achievedMinutes / achievedNumCuts,
        objetivo: showTotalHours === 'total' ? data.objective.time_hours : (data.objective.time_hours * 60) / baseNumCuts,
        goalMet: data.goal_met,
      };
    }), [objective.report, objective.base_metrics, objective.achieved_metrics, hiddenIndicators, showTotalHours]);
  
  // Export to PDF function - Compact single-page landscape design (charts on page 2 if enabled)
  const exportToPDF = () => {
    const goalsMetCount = objective.report.summary.goals_met;
    const totalGoals = objective.report.summary.total_goals;
    const successRate = Math.round((goalsMetCount / totalGoals) * 100);
    
    // Check if chart is enabled - if so, generate second page
    const includeChartPage = showChart && chartData.length > 0;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${objective.name}</title>
        <style>
          @page { size: A4 landscape; margin: 8mm; }
          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .page-break { page-break-before: always; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', -apple-system, sans-serif; 
            background: #fff; 
            color: #1a1a2e; 
            font-size: 10px;
            line-height: 1.3;
          }
          
          /* Main Container - compact */
          .container {
            padding: 12px 16px;
            max-width: 100%;
            height: 100%;
          }
          
          /* Header Section - compact */
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 8px;
            border-bottom: 2px solid #1a1a2e;
            margin-bottom: 10px;
          }
          .header-left h1 {
            font-size: 18px;
            font-weight: 800;
            color: #1a1a2e;
            margin-bottom: 2px;
          }
          .header-left .subtitle {
            font-size: 9px;
            color: #64748b;
            font-weight: 500;
          }
          .score-box {
            display: inline-block;
            padding: 8px 16px;
            background: #1a1a2e;
            color: white;
            border-radius: 6px;
            text-align: center;
          }
          .score-number {
            font-size: 22px;
            font-weight: 800;
            line-height: 1;
          }
          .score-label {
            font-size: 8px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            opacity: 0.8;
            margin-top: 2px;
          }
          
          /* Periods Row - inline compact */
          .periods-row {
            display: flex;
            gap: 12px;
            margin-bottom: 10px;
          }
          .period-card {
            flex: 1;
            padding: 8px 12px;
            border-radius: 6px;
            border: 1px solid;
          }
          .period-card.base {
            border-color: #64748b;
            background: #f8fafc;
          }
          .period-card.achieved {
            border-color: #1a1a2e;
            background: #1a1a2e;
            color: white;
          }
          .period-label {
            font-size: 7px;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-weight: 700;
            margin-bottom: 2px;
          }
          .period-card.base .period-label { color: #64748b; }
          .period-card.achieved .period-label { color: rgba(255,255,255,0.7); }
          .period-dates {
            font-size: 14px;
            font-weight: 700;
          }
          .period-card.base .period-dates { color: #1a1a2e; }
          .period-days {
            font-size: 9px;
            margin-top: 2px;
          }
          .period-card.base .period-days { color: #64748b; }
          .period-card.achieved .period-days { opacity: 0.8; }
          
          /* Data Table - compact */
          .data-section {
            margin-bottom: 10px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            border: 1px solid #1a1a2e;
          }
          thead tr {
            background: #1a1a2e;
            color: white;
          }
          th {
            padding: 6px 10px;
            font-size: 8px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            text-align: center;
          }
          th:first-child {
            text-align: left;
            width: 32%;
          }
          tbody tr {
            border-bottom: 1px solid #e2e8f0;
          }
          tbody tr:nth-child(odd) {
            background: #f8fafc;
          }
          tbody tr:nth-child(even) {
            background: #fff;
          }
          td {
            padding: 6px 10px;
            text-align: center;
            font-size: 10px;
          }
          td:first-child {
            text-align: left;
            font-weight: 600;
            color: #1a1a2e;
          }
          .value-base {
            color: #64748b;
            font-weight: 500;
          }
          .value-achieved {
            color: #1a1a2e;
            font-weight: 700;
          }
          
          /* Result Badges - compact */
          .badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 12px;
            font-size: 9px;
            font-weight: 700;
          }
          .badge-success {
            background: #1a1a2e;
            color: white;
          }
          .badge-error {
            background: white;
            color: #1a1a2e;
            border: 1px solid #1a1a2e;
          }
          .badge-icon {
            margin-right: 2px;
          }
          
          /* Summary Cards - compact inline */
          .summary-row {
            display: flex;
            gap: 8px;
            margin-top: 10px;
          }
          .summary-card {
            flex: 1;
            padding: 8px;
            border-radius: 6px;
            text-align: center;
            border: 1px solid #1a1a2e;
          }
          .summary-card.highlight {
            background: #1a1a2e;
            color: white;
          }
          .summary-value {
            font-size: 16px;
            font-weight: 800;
            line-height: 1;
          }
          .summary-label {
            font-size: 7px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 3px;
          }
          .summary-card:not(.highlight) .summary-label {
            color: #64748b;
          }
          .summary-card.highlight .summary-label {
            opacity: 0.8;
          }
          
          /* Footer - compact */
          .footer {
            margin-top: 10px;
            padding-top: 6px;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 8px;
            color: #94a3b8;
          }
          .footer-logo {
            font-weight: 700;
            color: #1a1a2e;
            font-size: 9px;
          }
          
          /* Chart Page Styles */
          .chart-page {
            padding: 20px;
          }
          .chart-page h2 {
            font-size: 20px;
            font-weight: 800;
            color: #1a1a2e;
            margin-bottom: 20px;
            text-align: center;
          }
          .chart-container {
            display: flex;
            gap: 20px;
            justify-content: center;
            flex-wrap: wrap;
          }
          .chart-box {
            flex: 1;
            min-width: 280px;
            max-width: 350px;
            border: 2px solid #1a1a2e;
            border-radius: 8px;
            padding: 16px;
            text-align: center;
          }
          .chart-box h3 {
            font-size: 12px;
            font-weight: 700;
            margin-bottom: 12px;
            color: #1a1a2e;
          }
          .chart-bar {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
          }
          .chart-bar-label {
            width: 100px;
            text-align: right;
            font-size: 9px;
            font-weight: 600;
          }
          .chart-bar-track {
            flex: 1;
            height: 20px;
            background: #f1f5f9;
            border-radius: 4px;
            overflow: hidden;
            position: relative;
          }
          .chart-bar-fill {
            height: 100%;
            border-radius: 4px;
          }
          .chart-bar-fill.base { background: #64748b; }
          .chart-bar-fill.achieved { background: #10b981; }
          .chart-bar-fill.objetivo { background: #8b5cf6; }
          .chart-bar-value {
            width: 50px;
            font-size: 9px;
            font-weight: 700;
          }
          .chart-legend {
            display: flex;
            justify-content: center;
            gap: 16px;
            margin-top: 16px;
            font-size: 9px;
          }
          .chart-legend-item {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .chart-legend-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
          }
          .chart-legend-dot.base { background: #64748b; }
          .chart-legend-dot.achieved { background: #10b981; }
          .chart-legend-dot.objetivo { background: #8b5cf6; }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <div class="header-left">
              <h1>${objective.name}</h1>
              <div class="subtitle">Relatório de Comparação de Objetivos</div>
            </div>
            <div class="header-right">
              <div class="score-box">
                <div class="score-number">${goalsMetCount}/${totalGoals}</div>
                <div class="score-label">Metas Atingidas (${successRate}%)</div>
              </div>
            </div>
          </div>
          
          <!-- Periods -->
          <div class="periods-row">
            <div class="period-card base">
              <div class="period-label">Período Base</div>
              <div class="period-dates">${formatDateShort(objective.base_period_start)} → ${formatDateShort(objective.base_period_end)}</div>
              <div class="period-days">${baseDays} dia(s) analisado(s)</div>
            </div>
            <div class="period-card achieved">
              <div class="period-label">Período de Comparação</div>
              <div class="period-dates">${formatDateShort(objective.comparison_period_start)} → ${formatDateShort(objective.comparison_period_end)}</div>
              <div class="period-days">${compDays} dia(s) analisado(s)</div>
            </div>
          </div>
          
          <!-- Data Table -->
          <div class="data-section">
            <table>
              <thead>
                <tr>
                  <th>Indicador</th>
                  <th>Real (Base)</th>
                  <th>Alcançado</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                ${['tempo_morto_entre', 'tempo_morto_intra', 'maquina_trabalhar'].filter(ind => !hiddenIndicators?.includes(ind)).map(indicator => {
                  const data = objective.report[indicator];
                  const baseNumCuts = objective.base_metrics?.num_marcadas || 1;
                  const achievedNumCuts = objective.achieved_metrics?.num_marcadas || 1;
                  const baseMinutes = data.base.time_hours * 60;
                  const achievedMinutes = data.achieved.time_hours * 60;
                  const baseValue = showTotalHours === 'total' ? data.base.time_hours : baseMinutes / baseNumCuts;
                  const achievedValue = showTotalHours === 'total' ? data.achieved.time_hours : achievedMinutes / achievedNumCuts;
                  const diffSign = data.diff_vs_objective_pct >= 0 ? '+' : '';
                  return `
                    <tr>
                      <td>${INDICATOR_LABELS[indicator]}</td>
                      <td class="value-base">${showTotalHours === 'total' ? formatHoursAsHHMM(baseValue) : formatMinutes(baseValue) + ' min'}</td>
                      <td class="value-achieved">${showTotalHours === 'total' ? formatHoursAsHHMM(achievedValue) : formatMinutes(achievedValue) + ' min'}</td>
                      <td>
                        <span class="badge ${data.goal_met ? 'badge-success' : 'badge-error'}">
                          <span class="badge-icon">${data.goal_met ? '✓' : '✗'}</span>
                          ${diffSign}${formatNumber(data.diff_vs_objective_pct)}%
                        </span>
                      </td>
                    </tr>
                  `;
                }).join('')}
                ${['num_marcadas', 'perimetro', 'velocidade'].filter(ind => !hiddenIndicators?.includes(ind)).map(indicator => {
                  const data = objective.report[indicator];
                  const unit = INDICATOR_UNITS[indicator];
                  const diffSign = data.diff_vs_objective_pct >= 0 ? '+' : '';
                  return `
                    <tr>
                      <td>${INDICATOR_LABELS[indicator]}</td>
                      <td class="value-base">${formatNumber(data.base, indicator === 'velocidade' ? 2 : 0)} ${unit}</td>
                      <td class="value-achieved">${formatNumber(data.achieved, indicator === 'velocidade' ? 2 : 0)} ${unit}</td>
                      <td>
                        <span class="badge ${data.goal_met ? 'badge-success' : 'badge-error'}">
                          <span class="badge-icon">${data.goal_met ? '✓' : '✗'}</span>
                          ${diffSign}${formatNumber(data.diff_vs_objective_pct)}%
                        </span>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          
          <!-- Summary Cards -->
          <div class="summary-row">
            <div class="summary-card">
              <div class="summary-value">${baseDays}</div>
              <div class="summary-label">Dias Base</div>
            </div>
            <div class="summary-card">
              <div class="summary-value">${compDays}</div>
              <div class="summary-label">Dias Comparação</div>
            </div>
            <div class="summary-card">
              <div class="summary-value">${objective.base_metrics?.num_marcadas || 0}</div>
              <div class="summary-label">Cortes Base</div>
            </div>
            <div class="summary-card">
              <div class="summary-value">${objective.achieved_metrics?.num_marcadas || 0}</div>
              <div class="summary-label">Cortes Alcançados</div>
            </div>
            <div class="summary-card highlight">
              <div class="summary-value">${successRate}%</div>
              <div class="summary-label">Taxa de Sucesso</div>
            </div>
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-logo">SAMIDEL - Sistema de Gestão Têxtil</div>
            <div>Gerado em ${new Date().toLocaleDateString('pt-PT')} às ${new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        </div>
        
        ${includeChartPage ? `
        <!-- Page 2: Charts -->
        <div class="page-break"></div>
        <div class="chart-page">
          <h2>Gráficos de Comparação - ${objective.name}</h2>
          <div class="chart-container">
            ${chartData.map(item => {
              const maxVal = Math.max(item.base, item.alcancado, item.objetivo || 0);
              const baseWidth = maxVal > 0 ? (item.base / maxVal * 100) : 0;
              const alcWidth = maxVal > 0 ? (item.alcancado / maxVal * 100) : 0;
              const objWidth = maxVal > 0 && item.objetivo ? (item.objetivo / maxVal * 100) : 0;
              return `
                <div class="chart-box">
                  <h3>${item.fullName}</h3>
                  <div class="chart-bar">
                    <span class="chart-bar-label">Base</span>
                    <div class="chart-bar-track">
                      <div class="chart-bar-fill base" style="width: ${baseWidth}%"></div>
                    </div>
                    <span class="chart-bar-value">${showTotalHours === 'total' ? item.base.toFixed(2) + 'h' : item.base.toFixed(1) + ' min'}</span>
                  </div>
                  ${showObjectiveColumn && item.objetivo ? `
                  <div class="chart-bar">
                    <span class="chart-bar-label">Objetivo</span>
                    <div class="chart-bar-track">
                      <div class="chart-bar-fill objetivo" style="width: ${objWidth}%"></div>
                    </div>
                    <span class="chart-bar-value">${showTotalHours === 'total' ? item.objetivo.toFixed(2) + 'h' : item.objetivo.toFixed(1) + ' min'}</span>
                  </div>
                  ` : ''}
                  <div class="chart-bar">
                    <span class="chart-bar-label">Alcançado</span>
                    <div class="chart-bar-track">
                      <div class="chart-bar-fill achieved" style="width: ${alcWidth}%"></div>
                    </div>
                    <span class="chart-bar-value">${showTotalHours ? item.alcancado.toFixed(2) + 'h' : item.alcancado.toFixed(1) + ' min'}</span>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
          <div class="chart-legend">
            <div class="chart-legend-item"><span class="chart-legend-dot base"></span> Real (Base)</div>
            ${showObjectiveColumn ? '<div class="chart-legend-item"><span class="chart-legend-dot objetivo"></span> Objetivo</div>' : ''}
            <div class="chart-legend-item"><span class="chart-legend-dot achieved"></span> Alcançado</div>
          </div>
          <div class="footer" style="margin-top: 30px;">
            <div class="footer-logo">SAMIDEL - Sistema de Gestão Têxtil</div>
            <div>Página 2 - Gráficos | Gerado em ${new Date().toLocaleDateString('pt-PT')}</div>
          </div>
        </div>
        ` : ''}
      </body>
      </html>
    `; 
    
    const printWindow = safePrintHTML(printContent);
    if (!printWindow) {
      toast.error('Popup bloqueado. Permita popups para exportar.');
    }
  };
  
  return (
    <div className="space-y-6" ref={reportRef}>
      {/* Controls Row - Impactful Design */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-gradient-to-r from-slate-50 to-slate-100 dark:from-zinc-800 dark:to-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-700">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-slate-700 dark:text-zinc-300">Opções:</span>
          
          {/* Toggle Mode - 3 states */}
          <div className="flex rounded-xl overflow-hidden border-2 border-slate-300 dark:border-zinc-600">
            <button
              type="button"
              onClick={() => setShowTotalHours('total')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all ${
                showTotalHours === 'total' 
                  ? 'bg-violet-500 text-white' 
                  : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'
              }`}
            >
              <Clock className="w-3.5 h-3.5" />
              HH:MM
            </button>
            <button
              type="button"
              onClick={() => setShowTotalHours('percent')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all ${
                showTotalHours === 'percent' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'
              }`}
            >
              %
            </button>
            <button
              type="button"
              onClick={() => setShowTotalHours('avg')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all ${
                showTotalHours === 'avg' 
                  ? 'bg-sky-500 text-white' 
                  : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'
              }`}
            >
              Média
            </button>
          </div>
          
          {/* Toggle Objective Column */}
          <button
            type="button"
            onClick={() => setShowObjectiveColumn(!showObjectiveColumn)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm border-2 ${
              showObjectiveColumn 
                ? 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-500/20 dark:text-violet-400 dark:border-violet-500/50' 
                : 'bg-slate-100 text-slate-500 border-slate-300 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-600'
            }`}
          >
            <Columns className="w-4 h-4" />
            {showObjectiveColumn ? 'Objetivo Visível' : 'Objetivo Oculto'}
          </button>
          
        </div>
        
        <div className="flex items-center gap-2">
          <Button onClick={exportToPDF} className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-lg shadow-red-200 dark:shadow-red-900/30" size="sm">
            <FileDown className="w-4 h-4 mr-2" /> Exportar PDF
          </Button>
          <Button onClick={() => onEdit(objective)} variant="outline" size="sm" className="border-2">
            <Edit2 className="w-4 h-4 mr-1" /> Editar
          </Button>
        </div>
      </div>
      
      {/* Period Summary - Prominent Display */}
      <div className="flex items-center justify-center gap-8 p-5 rounded-2xl bg-gradient-to-r from-slate-100 via-white to-emerald-100 dark:from-zinc-800 dark:via-zinc-900 dark:to-emerald-900/30 border-2 border-slate-200 dark:border-zinc-700">
        <div className="text-center">
          <p className="text-xs font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Período Base</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black text-slate-800 dark:text-white">
              {formatDateShort(objective.base_period_start)}
            </span>
            <ArrowRight className="w-5 h-5 text-slate-400" />
            <span className="text-2xl font-black text-slate-800 dark:text-white">
              {formatDateShort(objective.base_period_end)}
            </span>
          </div>
          <span className="inline-block mt-2 px-3 py-1 bg-slate-200 dark:bg-zinc-700 rounded-full text-sm font-bold text-slate-600 dark:text-zinc-300">{baseDays} dias</span>
        </div>
        
        <div className="flex flex-col items-center gap-1">
          <ArrowLeftRight className="w-8 h-8 text-violet-500" />
          <span className="text-xs font-semibold text-violet-500">vs</span>
        </div>
        
        <div className="text-center">
          <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">Período Alcançado</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
              {formatDateShort(objective.comparison_period_start)}
            </span>
            <ArrowRight className="w-5 h-5 text-emerald-400" />
            <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
              {formatDateShort(objective.comparison_period_end)}
            </span>
          </div>
          <span className="inline-block mt-2 px-3 py-1 bg-emerald-100 dark:bg-emerald-500/20 rounded-full text-sm font-bold text-emerald-700 dark:text-emerald-400">{compDays} dias</span>
        </div>
      </div>
      
      {/* Impactful Data Table */}
      <div className="overflow-hidden rounded-2xl border-2 border-slate-200 dark:border-zinc-700 shadow-xl">
        {/* Table Header */}
        <table className="w-full">
          <thead>
            <tr>
              <th className="bg-gradient-to-br from-slate-800 to-slate-900 dark:from-zinc-900 dark:to-black p-4 text-left text-white w-[30%]">
                <span className="font-bold text-base">INDICADOR</span>
              </th>
              <th className="bg-gradient-to-br from-slate-600 to-slate-700 p-4 text-center text-white w-[25%]">
                <span className="font-bold text-base">REAL (Base)</span>
              </th>
              {showObjectiveColumn && (
                <th className="bg-gradient-to-br from-violet-500 to-violet-600 p-4 text-center text-white w-[20%]">
                  <span className="font-bold text-base">OBJETIVO</span>
                </th>
              )}
              <th className={`bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 text-center text-white ${showObjectiveColumn ? 'w-[25%]' : 'w-[45%]'}`}>
                <span className="font-bold text-base">ALCANÇADO</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {['tempo_morto_entre', 'tempo_morto_intra', 'maquina_trabalhar'].map((indicator, idx) => {
              if (hiddenIndicators?.includes(indicator)) return null;
              const data = objective.report[indicator];
              const baseNumCuts = objective.base_metrics?.num_marcadas || 1;
              const achievedNumCuts = objective.achieved_metrics?.num_marcadas || 1;
              const baseMinutes = data.base.time_hours * 60;
              const objMinutes = data.objective.time_hours * 60;
              const achievedMinutes = data.achieved.time_hours * 60;
              const baseValue = showTotalHours === 'total' ? data.base.time_hours : (showTotalHours === 'avg' ? baseMinutes / baseNumCuts : 0);
              const objValue = showTotalHours === 'total' ? data.objective.time_hours : (showTotalHours === 'avg' ? objMinutes / baseNumCuts : 0);
              const achievedValue = showTotalHours === 'total' ? data.achieved.time_hours : (showTotalHours === 'avg' ? achievedMinutes / achievedNumCuts : 0);
              
              const formatVal = (val, pct, isTime) => {
                if (showTotalHours === 'percent') return `${formatNumber(pct)}%`;
                if (showTotalHours === 'avg') return isTime ? `${formatMinutes(val)} min` : formatNumber(val, indicator === 'velocidade' ? 2 : 0);
                return isTime ? formatHoursAsHHMM(val) : formatNumber(val, indicator === 'velocidade' ? 2 : 0);
              };
              const isTimeIndicator = ['tempo_morto_entre', 'tempo_morto_intra', 'maquina_trabalhar'].includes(indicator);
              const unit = INDICATOR_UNITS[indicator];
              
              return (
                <tr key={indicator} className={`${idx % 2 ? 'bg-slate-50 dark:bg-zinc-800/30' : 'bg-white dark:bg-zinc-900'} hover:bg-slate-100 dark:hover:bg-zinc-800/50 transition-colors`}>
                  <td className="p-4 border-r border-slate-200 dark:border-zinc-700">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800 dark:text-white">{INDICATOR_LABELS[indicator]}</span>
                      <button onClick={() => toggleIndicator(indicator)} className="p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-400">
                        <EyeOff className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="p-4 text-center border-r border-slate-200 dark:border-zinc-700">
                    <span className="text-lg font-semibold text-slate-700 dark:text-zinc-300">
                      {isTimeIndicator ? formatVal(baseValue, data.base.pct, true) : `${formatNumber(data.base.value, indicator === 'velocidade' ? 2 : 0)} ${unit}`}
                    </span>
                    {showTotalHours !== 'percent' && <span className="block text-xs text-slate-500 dark:text-zinc-400">{formatNumber(data.base.pct)}%</span>}
                  </td>
                  {showObjectiveColumn && (
                    <td className="p-4 text-center border-r border-violet-200 dark:border-violet-500/30 bg-violet-50/50 dark:bg-violet-900/10">
                      <span className="text-lg font-bold text-violet-600 dark:text-violet-400">
                        {isTimeIndicator ? formatVal(objValue, data.objective.pct, true) : `${formatNumber(data.objective.value, indicator === 'velocidade' ? 2 : 0)} ${unit}`}
                      </span>
                      {showTotalHours !== 'percent' && <span className="block text-xs text-violet-500">{formatNumber(data.objective.pct)}%</span>}
                    </td>
                  )}
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="text-center">
                        <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                          {isTimeIndicator ? formatVal(achievedValue, data.achieved.pct, true) : `${formatNumber(data.achieved.value, indicator === 'velocidade' ? 2 : 0)} ${unit}`}
                        </span>
                        {showTotalHours !== 'percent' && <span className="block text-xs text-emerald-500">{formatNumber(data.achieved.pct)}%</span>}
                      </div>
                      <span className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-bold shadow-sm ${
                        data.goal_met 
                          ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-emerald-200 dark:shadow-emerald-900/30' 
                          : 'bg-gradient-to-r from-red-400 to-red-500 text-white shadow-red-200 dark:shadow-red-900/30'
                      }`}>
                        {data.goal_met ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {data.diff_vs_objective_pct >= 0 ? '+' : ''}{formatNumber(data.diff_vs_objective_pct)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
            
            {/* Non-time indicators */}
            {['num_marcadas', 'perimetro', 'velocidade'].map((indicator, idx) => {
              if (hiddenIndicators?.includes(indicator)) return null;
              const data = objective.report[indicator];
              const unit = INDICATOR_UNITS[indicator];
              
              return (
                <tr key={indicator} className={`${(idx + 3) % 2 ? 'bg-slate-50 dark:bg-zinc-800/30' : 'bg-white dark:bg-zinc-900'} hover:bg-slate-100 dark:hover:bg-zinc-800/50 transition-colors`}>
                  <td className="p-4 border-r border-slate-200 dark:border-zinc-700">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-800 dark:text-white">{INDICATOR_LABELS[indicator]}</span>
                      <button onClick={() => toggleIndicator(indicator)} className="p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-400">
                        <EyeOff className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="p-4 text-center border-r border-slate-200 dark:border-zinc-700">
                    <span className="text-lg font-semibold text-slate-700 dark:text-zinc-300">{formatNumber(data.base, indicator === 'velocidade' ? 2 : 0)} {unit}</span>
                  </td>
                  {showObjectiveColumn && (
                    <td className="p-4 text-center border-r border-violet-200 dark:border-violet-500/30 bg-violet-50/50 dark:bg-violet-900/10">
                      <span className="text-lg font-bold text-violet-600 dark:text-violet-400">{formatNumber(data.objective, indicator === 'velocidade' ? 2 : 0)} {unit}</span>
                    </td>
                  )}
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatNumber(data.achieved, indicator === 'velocidade' ? 2 : 0)} {unit}</span>
                      <span className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-bold shadow-sm ${
                        data.goal_met 
                          ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 text-white shadow-emerald-200 dark:shadow-emerald-900/30' 
                          : 'bg-gradient-to-r from-red-400 to-red-500 text-white shadow-red-200 dark:shadow-red-900/30'
                      }`}>
                        {data.goal_met ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        {data.diff_vs_objective_pct >= 0 ? '+' : ''}{formatNumber(data.diff_vs_objective_pct)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Hidden Indicators Restore */}
      {hiddenIndicators?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-500/30">
          <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">Indicadores ocultos:</span>
          {hiddenIndicators.map(ind => (
            <button
              key={ind}
              onClick={() => toggleIndicator(ind)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-zinc-800 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-zinc-700 border border-amber-300 dark:border-amber-500/50 shadow-sm"
            >
              <Eye className="w-3.5 h-3.5" />
              {INDICATOR_LABELS[ind]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

// Memoized Sortable Objective Card Component for performance
const SortableObjectiveCard = React.memo(function SortableObjectiveCard({ 
  objective, t, expandedObjective, setExpandedObjective, 
  showTotalHours, setShowTotalHours, onStatusChange, onCompare, onClone, onDelete, onEdit,
  hiddenIndicators, toggleIndicator,
  formatNumber, formatMinutes, formatHoursAsHHMM, getStatusLabel
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: objective.id });
  
  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }), [transform, transition, isDragging]);
  
  const objStatus = objective.objective_status || 'pendente';
  const statusConfig = STATUS_CONFIG[objStatus] || STATUS_CONFIG.pendente;
  const isExpanded = expandedObjective === objective.id;
  
  // Calculate days info - memoized
  const baseDays = useMemo(() => objective.base_metrics?.days_count || 0, [objective.base_metrics?.days_count]);
  const compDays = useMemo(() => objective.achieved_metrics?.days_count || 0, [objective.achieved_metrics?.days_count]);
  
  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`rounded-xl overflow-hidden transition-all duration-200 ${isDragging ? 'ring-2 ring-violet-500 shadow-xl' : 'shadow-md hover:shadow-lg'} ${
        isExpanded ? 'bg-white dark:bg-zinc-900 border-2 border-slate-300 dark:border-zinc-600' : 'bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700'
      }`}
    >
      {/* Objective Header - Clickable Card */}
      <div 
        className={`p-4 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50 dark:bg-zinc-800/50' : 'hover:bg-slate-50/50 dark:hover:bg-zinc-800/30'}`}
        onClick={() => setExpandedObjective(isExpanded ? null : objective.id)}
      >
        <div className="flex items-center gap-3">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-zinc-700 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300"
          >
            <GripVertical className="w-5 h-5" />
          </button>
          
          {/* Status Indicator Bar */}
          <div className={`w-1.5 h-12 rounded-full ${statusConfig.color}`} />
          
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="font-bold text-slate-900 dark:text-white text-base truncate">{objective.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${statusConfig.bgLight} ${statusConfig.textColor}`}>
                    <span className={`w-2 h-2 rounded-full ${statusConfig.color}`} />
                    {statusConfig.label}
                    {objective.report?.summary && (
                      <span className="ml-1 opacity-80">({objective.report.summary.goals_met}/{objective.report.summary.total_goals})</span>
                    )}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-zinc-400">
                    <Clock className="w-3.5 h-3.5" />
                    {baseDays} dias base
                    {compDays > 0 && <span className="text-emerald-600 dark:text-emerald-400">• {compDays} dias comparação</span>}
                  </span>
                </div>
              </div>
              
              {/* Expand Indicator */}
              <div className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all ${
                isExpanded 
                  ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' 
                  : 'bg-slate-100 dark:bg-zinc-800 text-slate-400 dark:text-zinc-500'
              }`}>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </div>
            </div>
            
            {/* Period Info - Compact */}
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-zinc-400">
              <span className="font-medium">Base: <span className="text-slate-800 dark:text-zinc-300">{objective.base_period_start} → {objective.base_period_end}</span></span>
              {objective.comparison_period_start && (
                <span className="font-medium">Comparação: <span className="text-emerald-600 dark:text-emerald-400">{objective.comparison_period_start} → {objective.comparison_period_end}</span></span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-zinc-700">
          {/* Status Change Buttons */}
          <div className="px-4 py-3 bg-slate-50/50 dark:bg-zinc-800/30 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-600 dark:text-zinc-400 mr-2">Alterar Estado:</span>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <button
                key={key}
                onClick={() => onStatusChange(objective, key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  objStatus === key 
                    ? `${config.bgLight} ${config.textColor} ring-2 ring-current ring-offset-1` 
                    : 'bg-white dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200 border border-slate-200 dark:border-zinc-600'
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>

          {/* Data Table */}
          <div className="p-4">
            {objective.report ? (
              <ObjectiveReportView 
                objective={objective}
                showTotalHours={showTotalHours}
                setShowTotalHours={setShowTotalHours}
                hiddenIndicators={hiddenIndicators}
                toggleIndicator={toggleIndicator}
                onEdit={onEdit}
                formatNumber={formatNumber}
                formatMinutes={formatMinutes}
                formatHoursAsHHMM={formatHoursAsHHMM}
              />
            ) : (
              /* Show base metrics and objectives even without comparison */
              <div className="space-y-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex rounded-xl overflow-hidden border-2 border-slate-300 dark:border-zinc-600">
                    <button type="button" onClick={() => setShowTotalHours('total')} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all ${showTotalHours === 'total' ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>HH:MM</button>
                    <button type="button" onClick={() => setShowTotalHours('percent')} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold transition-all ${showTotalHours === 'percent' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'}`}>%</button>
                  </div>
                  <Button onClick={() => onEdit(objective)} variant="outline" size="sm" className="border-2">
                    <Edit2 className="w-4 h-4 mr-1" /> Editar Objetivo
                  </Button>
                </div>
                
                {objective.base_metrics ? (
                  <div className="overflow-x-auto rounded-2xl border-2 border-slate-200 dark:border-zinc-700 shadow-lg">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-slate-800 to-slate-700 text-white">
                          <th className="p-4 text-left font-bold uppercase text-xs tracking-wider">INDICADOR</th>
                          <th className="p-4 text-center font-bold uppercase text-xs tracking-wider">REAL (Base)</th>
                          <th className="p-4 text-center font-bold uppercase text-xs tracking-wider bg-violet-600/50">OBJETIVO</th>
                        </tr>
                      </thead>
                      <tbody>
                        {['tempo_morto_entre', 'tempo_morto_intra', 'maquina_trabalhar'].map((indicator, idx) => {
                          const bm = objective.base_metrics;
                          const ov = objective.objective_values || {};
                          const baseHours = bm[`${indicator}_hours`] || 0;
                          const basePct = bm[`${indicator}_pct`] || 0;
                          const targetPct = ov[`${indicator}_pct`] ?? basePct;
                          const targetHours = ov[`${indicator}_hours`] ?? baseHours;
                          return (
                            <tr key={indicator} className={`${idx % 2 ? 'bg-slate-50 dark:bg-zinc-800/30' : 'bg-white dark:bg-zinc-900'}`}>
                              <td className="p-4 border-r border-slate-200 dark:border-zinc-700 font-bold text-slate-800 dark:text-white">{INDICATOR_LABELS[indicator]}</td>
                              <td className="p-4 text-center border-r border-slate-200 dark:border-zinc-700">
                                <span className="text-lg font-semibold text-slate-700 dark:text-zinc-300">
                                  {showTotalHours === 'percent' ? `${formatNumber(basePct)}%` : formatHoursAsHHMM(baseHours)}
                                </span>
                                {showTotalHours !== 'percent' && <span className="block text-xs text-slate-500">{formatNumber(basePct)}%</span>}
                              </td>
                              <td className="p-4 text-center bg-violet-50/50 dark:bg-violet-900/10">
                                <span className="text-lg font-bold text-violet-600 dark:text-violet-400">
                                  {showTotalHours === 'percent' ? `${formatNumber(targetPct)}%` : formatHoursAsHHMM(targetHours)}
                                </span>
                                {showTotalHours !== 'percent' && <span className="block text-xs text-violet-500">{formatNumber(targetPct)}%</span>}
                              </td>
                            </tr>
                          );
                        })}
                        {['num_marcadas', 'perimetro', 'velocidade'].map((indicator, idx) => {
                          const bm = objective.base_metrics;
                          const val = indicator === 'num_marcadas' ? bm.num_marcadas : (indicator === 'perimetro' ? bm.perimetro : bm.velocidade);
                          const unit = INDICATOR_UNITS[indicator];
                          return (
                            <tr key={indicator} className={`${(idx + 3) % 2 ? 'bg-slate-50 dark:bg-zinc-800/30' : 'bg-white dark:bg-zinc-900'}`}>
                              <td className="p-4 border-r border-slate-200 dark:border-zinc-700 font-bold text-slate-800 dark:text-white">{INDICATOR_LABELS[indicator]}</td>
                              <td className="p-4 text-center border-r border-slate-200 dark:border-zinc-700">
                                <span className="text-lg font-semibold text-slate-700 dark:text-zinc-300">{formatNumber(val, indicator === 'velocidade' ? 2 : 0)} {unit}</span>
                              </td>
                              <td className="p-4 text-center bg-violet-50/50 dark:bg-violet-900/10">
                                <span className="text-lg font-bold text-violet-600 dark:text-violet-400">{formatNumber(val, indicator === 'velocidade' ? 2 : 0)} {unit}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6 rounded-xl bg-slate-100 dark:bg-zinc-800 text-center">
                    <p className="text-slate-500">Sem dados base disponíveis</p>
                  </div>
                )}
                
                <div className="p-4 rounded-xl bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-700">
                  <p className="text-sm text-sky-700 dark:text-sky-300">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Compare com um período para ver os resultados alcançados.
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-4 border-t border-slate-200 dark:border-zinc-700 mt-4 flex-wrap">
              {!objective.report && (
                <Button onClick={() => onCompare(objective)} className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600" size="sm">
                  <Calendar className="w-4 h-4" /> Comparar Período
                </Button>
              )}
              {objective.report && objective.status !== 'achieved' && (
                <Button onClick={() => onCompare(objective)} variant="outline" className="flex items-center gap-2" size="sm">
                  <RefreshCw className="w-4 h-4" /> Recomparar
                </Button>
              )}
              {objective.achieved_metrics && (
                <Button onClick={() => onClone(objective)} variant="outline" className="flex items-center gap-2" size="sm">
                  <Copy className="w-4 h-4" /> Usar como Nova Base
                </Button>
              )}
              <Button onClick={() => onDelete(objective)} variant="ghost" className="flex items-center gap-2 text-red-500 hover:text-red-600 hover:bg-red-500/10 ml-auto" size="sm">
                <Trash2 className="w-4 h-4" /> Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

function ObjectivesManagement({ t }) {
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [expandedObjective, setExpandedObjective] = useState(null);
  const [showTotalHours, setShowTotalHours] = useState('total'); // 'total', 'percent', 'avg'
  const [statusFilter, setStatusFilter] = useState('all');
  const [hiddenIndicators, setHiddenIndicators] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Toggle indicator visibility
  const toggleIndicator = (indicator) => {
    setHiddenIndicators(prev => 
      prev.includes(indicator) 
        ? prev.filter(i => i !== indicator)
        : [...prev, indicator]
    );
  };
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Form state
  const [formName, setFormName] = useState('');
  const [basePeriodStart, setBasePeriodStart] = useState('');
  const [basePeriodEnd, setBasePeriodEnd] = useState('');
  const [baseMetrics, setBaseMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [targets, setTargets] = useState([]);
  
  // Comparison form state
  const [comparisonStart, setComparisonStart] = useState('');
  const [comparisonEnd, setComparisonEnd] = useState('');

  // Fetch objectives
  const fetchObjectives = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API_BASE}/objectives`);
      setObjectives(res.data);
    } catch (e) {
      toast.error('Erro ao carregar objetivos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchObjectives();
  }, [fetchObjectives]);

  // Fetch base period metrics
  const fetchBaseMetrics = async () => {
    if (!basePeriodStart || !basePeriodEnd) {
      toast.error('Selecione as datas do período base');
      return;
    }
    
    setLoadingMetrics(true);
    try {
      const params = new URLSearchParams();
      params.append('start_date', basePeriodStart);
      params.append('end_date', basePeriodEnd);
      
      const res = await api.get(`${API_BASE}/summary?${params.toString()}`);
      
      if (!res.data.has_data) {
        toast.error('Não existem dados para o período selecionado');
        return;
      }
      
      // Transform to comparison format
      const totalTime = res.data.total_cutting_time_min + res.data.total_machine_stopped_min + res.data.total_entre_cortes_min;
      const cuttingMin = res.data.total_cutting_time_min || 0;
      const stoppedMin = res.data.total_machine_stopped_min || 0;
      const entreMin = res.data.total_entre_cortes_min || 0;
      const corteIntraTotal = cuttingMin + stoppedMin; // Eficiência = Corte / (Corte + Intra)
      
      const metrics = {
        days_count: res.data.total_days,
        tempo_morto_entre_min: entreMin,
        tempo_morto_entre_hours: entreMin / 60,
        tempo_morto_entre_pct: totalTime > 0 ? (entreMin / totalTime * 100) : 0,
        tempo_morto_intra_min: stoppedMin,
        tempo_morto_intra_hours: stoppedMin / 60,
        tempo_morto_intra_pct: totalTime > 0 ? (stoppedMin / totalTime * 100) : 0,
        maquina_trabalhar_min: cuttingMin,
        maquina_trabalhar_hours: cuttingMin / 60,
        maquina_trabalhar_pct: corteIntraTotal > 0 ? (cuttingMin / corteIntraTotal * 100) : 0, // Eficiência = Corte / (Corte + Intra)
        num_marcadas: res.data.total_cuts,
        perimetro: res.data.total_perimeter,
        velocidade: res.data.weighted_avg_speed || res.data.avg_real_speed || res.data.avg_speed || 0,
        total_time_min: totalTime,
        total_time_hours: totalTime / 60
      };
      
      setBaseMetrics(metrics);
      
      // Calculate base values for the new format
      const numCuts = res.data.total_cuts || 1;
      const avgEntreMarc = entreMin / numCuts; // Tempo médio entre marcadas em minutos
      const stoppedPctOfCutting = cuttingMin > 0 ? (stoppedMin / cuttingMin) * 100 : 0; // % parado vs corte
      
      // Initialize targets with the new format:
      // 1. Tempo médio entre marcadas em minutos
      // 2. % tempo parado em relação ao tempo de corte
      // 3. % tempo de corte efetivo em relação às horas de trabalho
      setTargets([
        { 
          indicator: 'tempo_morto_entre', 
          type: 'minutes_avg', 
          value: Math.round(avgEntreMarc * 10) / 10, // Default: current average
          baseAvg: avgEntreMarc
        },
        { 
          indicator: 'tempo_morto_intra', 
          type: 'pct_of_cutting', 
          value: Math.round(stoppedPctOfCutting), // Default: current %
          basePct: stoppedPctOfCutting
        },
        { 
          indicator: 'maquina_trabalhar', 
          type: 'pct_of_workday', 
          value: Math.round(metrics.maquina_trabalhar_pct), // Default: current %
          basePct: metrics.maquina_trabalhar_pct
        }
      ]);
      
      toast.success(`Dados carregados: ${res.data.total_days} dias, ${res.data.total_cuts} cortes`);
    } catch (e) {
      toast.error('Erro ao carregar métricas');
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Update a target
  const updateTarget = (indicator, field, value) => {
    setTargets(prev => prev.map(t => 
      t.indicator === indicator ? { ...t, [field]: value } : t
    ));
  };

  // Calculate objective value based on target
  const calculateObjectiveValue = (indicator, target) => {
    if (!baseMetrics) return 0;
    
    const baseValue = isTimeIndicator(indicator) 
      ? baseMetrics[`${indicator}_hours`] 
      : baseMetrics[indicator];
    
    if (target.type === 'absolute') {
      return target.value;
    } else {
      return baseValue * (1 + target.value / 100);
    }
  };

  // Create objective
  const handleCreate = async () => {
    if (!formName || !basePeriodStart || !basePeriodEnd || !baseMetrics) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      const res = await api.post(`${API_BASE}/objectives`, {
        name: formName,
        base_period_start: basePeriodStart,
        base_period_end: basePeriodEnd,
        targets: targets
      });
      
      toast.success('Objetivo criado com sucesso');
      setShowCreateModal(false);
      resetForm();
      fetchObjectives();
    } catch (e) {
      toast.error('Erro ao criar objetivo');
    }
  };

  // Compare objective
  const handleCompare = async () => {
    if (!selectedObjective || !comparisonStart || !comparisonEnd) {
      toast.error('Selecione as datas do período de comparação');
      return;
    }
    
    try {
      const res = await api.post(`${API_BASE}/objectives/${selectedObjective.id}/compare`, {
        comparison_period_start: comparisonStart,
        comparison_period_end: comparisonEnd
      });
      
      toast.success('Comparação realizada com sucesso');
      setShowCompareModal(false);
      setSelectedObjective(null);
      setComparisonStart('');
      setComparisonEnd('');
      fetchObjectives();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao comparar objetivo');
    }
  };

  // Clone achieved as new base
  const handleCloneAsBase = async (objective) => {
    try {
      const res = await api.post(`${API_BASE}/objectives/${objective.id}/clone-as-base`);
      toast.success('Novo objetivo criado baseado nos resultados alcançados');
      fetchObjectives();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar novo objetivo');
    }
  };

  // Delete objective
  const handleDelete = async (objective) => {
    if (!window.confirm('Tem certeza que deseja eliminar este objetivo?')) return;
    
    try {
      await api.delete(`${API_BASE}/objectives/${objective.id}`);
      toast.success('Objetivo eliminado');
      fetchObjectives();
    } catch (e) {
      toast.error('Erro ao eliminar objetivo');
    }
  };

  // Update objective status
  const handleStatusChange = async (objective, newStatus) => {
    try {
      await api.patch(`${API_BASE}/objectives/${objective.id}`, { 
        objective_status: newStatus 
      });
      toast.success(`Estado alterado para "${STATUS_CONFIG[newStatus]?.label || newStatus}"`);
      fetchObjectives();
    } catch (e) {
      toast.error('Erro ao atualizar estado');
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = filteredObjectives.findIndex(obj => obj.id === active.id);
    const newIndex = filteredObjectives.findIndex(obj => obj.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Optimistically update UI
    const newOrder = arrayMove(filteredObjectives, oldIndex, newIndex);
    setObjectives(prev => {
      const otherObjs = prev.filter(obj => !filteredObjectives.find(f => f.id === obj.id));
      return [...newOrder, ...otherObjs];
    });
    
    // Save order to backend
    try {
      const orderedIds = newOrder.map((obj, idx) => ({ id: obj.id, order: idx }));
      await api.post(`${API_BASE}/objectives/reorder`, { objectives: orderedIds });
    } catch (e) {
      fetchObjectives();
    }
  };

  // Filter objectives by status
  const filteredObjectives = objectives.filter(obj => {
    if (statusFilter === 'all') return true;
    const objStatus = obj.objective_status || 'pendente';
    return objStatus === statusFilter;
  });

  // Count by status
  const statusCounts = {
    all: objectives.length,
    ativa: objectives.filter(o => o.objective_status === 'ativa').length,
    iniciada: objectives.filter(o => o.objective_status === 'iniciada').length,
    pendente: objectives.filter(o => !o.objective_status || o.objective_status === 'pendente').length,
    arquivada: objectives.filter(o => o.objective_status === 'arquivada').length,
    concluida: objectives.filter(o => o.objective_status === 'concluida').length,
  };

  // Reset form
  const resetForm = () => {
    setFormName('');
    setBasePeriodStart('');
    setBasePeriodEnd('');
    setBaseMetrics(null);
    setTargets([]);
  };

  // Format number
  const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined) return '-';
    return Number(value).toLocaleString('pt-PT', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'achieved': return 'bg-emerald-500';
      case 'compared': return 'bg-amber-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'achieved': return 'Alcançado';
      case 'compared': return 'Parcial';
      case 'failed': return 'Não Alcançado';
      default: return 'Pendente';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border-2 border-slate-200 dark:border-zinc-700 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">Gestão de Objetivos</h2>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                {objectives.length} objetivo(s) registado(s) • Arraste para reordenar prioridade
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 shadow-lg"
          >
            <Plus className="w-4 h-4" />
            Novo Objetivo
          </Button>
        </div>
        
        {/* Status Filter Tabs */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-zinc-700">
          {[
            { key: 'all', label: 'Todos', icon: Filter },
            { key: 'ativa', label: 'Ativas', icon: Play },
            { key: 'iniciada', label: 'Iniciadas', icon: TrendingUp },
            { key: 'pendente', label: 'Pendentes', icon: Pause },
            { key: 'arquivada', label: 'Arquivadas', icon: Archive },
            { key: 'concluida', label: 'Concluídas', icon: Check },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                statusFilter === key
                  ? key === 'all' 
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-md'
                    : `${STATUS_CONFIG[key]?.bgLight || 'bg-slate-100'} ${STATUS_CONFIG[key]?.textColor || 'text-slate-600'} ring-2 ring-current`
                  : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-zinc-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                statusFilter === key ? 'bg-white/20 dark:bg-black/20' : 'bg-slate-200 dark:bg-zinc-700'
              }`}>
                {statusCounts[key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Objectives List */}
      {loading ? (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-700 p-12 text-center">
          <RefreshCw className="w-10 h-10 animate-spin mx-auto text-violet-500" />
          <p className="mt-3 text-slate-600 dark:text-zinc-400">A carregar objetivos...</p>
        </div>
      ) : filteredObjectives.length === 0 ? (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-700 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-zinc-800 mb-4">
            <Target className="w-8 h-8 text-slate-400 dark:text-zinc-500" />
          </div>
          <p className="text-lg font-semibold text-slate-700 dark:text-zinc-300">
            {objectives.length === 0 ? 'Nenhum objetivo criado' : `Nenhum objetivo "${STATUS_CONFIG[statusFilter]?.label || statusFilter}"`}
          </p>
          <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
            {objectives.length === 0 
              ? 'Crie um objetivo para começar a comparar períodos e acompanhar metas'
              : 'Altere o filtro ou crie um novo objetivo'
            }
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={filteredObjectives.map(obj => obj.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {filteredObjectives.map(objective => (
                <SortableObjectiveCard
                  key={objective.id}
                  objective={objective}
                  t={t}
                  expandedObjective={expandedObjective}
                  setExpandedObjective={setExpandedObjective}
                  showTotalHours={showTotalHours}
                  setShowTotalHours={setShowTotalHours}
                  hiddenIndicators={hiddenIndicators}
                  toggleIndicator={toggleIndicator}
                  onStatusChange={handleStatusChange}
                  onCompare={(obj) => {
                    setSelectedObjective(obj);
                    setShowCompareModal(true);
                  }}
                  onClone={handleCloneAsBase}
                  onDelete={handleDelete}
                  onEdit={(obj) => {
                    setSelectedObjective(obj);
                    setShowEditModal(true);
                  }}
                  formatNumber={formatNumber}
                  formatMinutes={formatMinutes}
                  formatHoursAsHHMM={formatHoursAsHHMM}
                  getStatusLabel={getStatusLabel}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${t.card} rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto`}>
            <div className="p-6 border-b border-gray-200 dark:border-zinc-700">
              <h2 className={`text-xl font-bold ${t.text}`}>Novo Objetivo</h2>
              <p className={`text-sm ${t.textMut}`}>Defina um período base e os objetivos a alcançar</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-2`}>Nome do Objetivo</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex: Objetivo Março 2026"
                  className={`w-full px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                />
              </div>

              {/* Base Period */}
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-2`}>Período Base</label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={basePeriodStart}
                    onChange={(e) => setBasePeriodStart(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                  />
                  <span className={t.textMut}>até</span>
                  <input
                    type="date"
                    value={basePeriodEnd}
                    onChange={(e) => setBasePeriodEnd(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                  />
                  <Button
                    onClick={fetchBaseMetrics}
                    disabled={loadingMetrics || !basePeriodStart || !basePeriodEnd}
                    className="bg-sky-500 hover:bg-sky-600"
                  >
                    {loadingMetrics ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Carregar'}
                  </Button>
                </div>
              </div>

              {/* Base Metrics Display */}
              {baseMetrics && (
                <div className={`p-4 rounded-lg ${t.inner}`}>
                  <h3 className={`text-sm font-medium ${t.text} mb-3`}>Métricas do Período Base ({baseMetrics.days_count} dias)</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className={`text-xs ${t.textMut}`}>Tempo Corte</p>
                      <p className={`text-lg font-bold text-emerald-500`}>{formatNumber(baseMetrics.maquina_trabalhar_hours, 2)}h ({formatNumber(baseMetrics.maquina_trabalhar_pct)}%)</p>
                    </div>
                    <div>
                      <p className={`text-xs ${t.textMut}`}>Tempo Intra Marc.</p>
                      <p className={`text-lg font-bold text-red-500`}>{formatNumber(baseMetrics.tempo_morto_intra_hours, 2)}h ({formatNumber(baseMetrics.tempo_morto_intra_pct)}%)</p>
                    </div>
                    <div>
                      <p className={`text-xs ${t.textMut}`}>Tempo Entre Marc.</p>
                      <p className={`text-lg font-bold text-amber-500`}>{formatNumber(baseMetrics.tempo_morto_entre_hours, 2)}h ({formatNumber(baseMetrics.tempo_morto_entre_pct)}%)</p>
                    </div>
                    <div>
                      <p className={`text-xs ${t.textMut}`}>N.º Marcadas</p>
                      <p className={`text-lg font-bold ${t.text}`}>{baseMetrics.num_marcadas}</p>
                    </div>
                    <div>
                      <p className={`text-xs ${t.textMut}`}>Perímetro</p>
                      <p className={`text-lg font-bold ${t.text}`}>{formatNumber(baseMetrics.perimetro, 0)} m</p>
                    </div>
                    <div>
                      <p className={`text-xs ${t.textMut}`}>Vel. Real Ponderada</p>
                      <p className={`text-lg font-bold ${t.text}`}>{formatNumber(baseMetrics.velocidade, 2)} m/min</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Targets - New Format */}
              {baseMetrics && targets.length > 0 && (
                <div>
                  <h3 className={`text-sm font-medium ${t.text} mb-3`}>Definir Objetivos</h3>
                  <div className="space-y-4">
                    {/* 1. Tempo Médio Entre Marcadas - em minutos */}
                    {targets.find(t => t.indicator === 'tempo_morto_entre') && (() => {
                      const target = targets.find(t => t.indicator === 'tempo_morto_entre');
                      const improved = target.value < target.baseAvg;
                      return (
                        <div className={`p-4 rounded-xl border-2 ${improved ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-gray-200 dark:border-zinc-700'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className={`text-sm font-semibold ${t.text}`}>Tempo Médio Entre Marcadas</p>
                              <p className={`text-xs ${t.textMut}`}>
                                Atual: <span className="font-medium text-amber-500">{formatNumber(target.baseAvg, 1)} min/corte</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs ${t.textMut}`}>Objetivo:</span>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={target.value}
                                  onChange={(e) => updateTarget('tempo_morto_entre', 'value', parseFloat(e.target.value) || 0)}
                                  step={0.5}
                                  min={0}
                                  className={`w-24 px-3 py-2 rounded-lg text-right text-lg font-bold ${t.inputBg} ${t.card} ${improved ? 'text-emerald-500' : t.text}`}
                                />
                                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${t.textMut}`}>min</span>
                              </div>
                            </div>
                          </div>
                          {improved && (
                            <p className="text-xs text-emerald-500 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Redução de {formatNumber(((target.baseAvg - target.value) / target.baseAvg) * 100, 0)}%
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* 2. % Tempo Parado em relação ao Tempo de Corte */}
                    {targets.find(t => t.indicator === 'tempo_morto_intra') && (() => {
                      const target = targets.find(t => t.indicator === 'tempo_morto_intra');
                      const improved = target.value < target.basePct;
                      return (
                        <div className={`p-4 rounded-xl border-2 ${improved ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-gray-200 dark:border-zinc-700'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className={`text-sm font-semibold ${t.text}`}>% Máquina Parada / Tempo de Corte</p>
                              <p className={`text-xs ${t.textMut}`}>
                                Atual: <span className="font-medium text-red-500">{formatNumber(target.basePct, 0)}%</span> do tempo de corte
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs ${t.textMut}`}>Objetivo:</span>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={target.value}
                                  onChange={(e) => updateTarget('tempo_morto_intra', 'value', parseFloat(e.target.value) || 0)}
                                  step={5}
                                  min={0}
                                  max={200}
                                  className={`w-24 px-3 py-2 rounded-lg text-right text-lg font-bold ${t.inputBg} ${t.card} ${improved ? 'text-emerald-500' : t.text}`}
                                />
                                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${t.textMut}`}>%</span>
                              </div>
                            </div>
                          </div>
                          {improved && (
                            <p className="text-xs text-emerald-500 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Redução de {formatNumber(target.basePct - target.value, 0)} pontos percentuais
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* 3. % Tempo de Corte Efetivo das Horas de Trabalho */}
                    {targets.find(t => t.indicator === 'maquina_trabalhar') && (() => {
                      const target = targets.find(t => t.indicator === 'maquina_trabalhar');
                      const improved = target.value > target.basePct;
                      return (
                        <div className={`p-4 rounded-xl border-2 ${improved ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-gray-200 dark:border-zinc-700'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className={`text-sm font-semibold ${t.text}`}>% Tempo de Corte Efetivo</p>
                              <p className={`text-xs ${t.textMut}`}>
                                Atual: <span className="font-medium text-emerald-500">{formatNumber(target.basePct, 0)}%</span> Corte / (Corte + Intra)
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs ${t.textMut}`}>Objetivo:</span>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={target.value}
                                  onChange={(e) => updateTarget('maquina_trabalhar', 'value', parseFloat(e.target.value) || 0)}
                                  step={5}
                                  min={0}
                                  max={100}
                                  className={`w-24 px-3 py-2 rounded-lg text-right text-lg font-bold ${t.inputBg} ${t.card} ${improved ? 'text-emerald-500' : t.text}`}
                                />
                                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${t.textMut}`}>%</span>
                              </div>
                            </div>
                          </div>
                          {improved && (
                            <p className="text-xs text-emerald-500 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Aumento de {formatNumber(target.value - target.basePct, 0)} pontos percentuais
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-zinc-700 flex justify-end gap-3">
              <Button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!formName || !baseMetrics}
                className="bg-violet-500 hover:bg-violet-600"
              >
                Criar Objetivo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Compare Modal */}
      {showCompareModal && selectedObjective && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${t.card} rounded-xl w-full max-w-md`}>
            <div className="p-6 border-b border-gray-200 dark:border-zinc-700">
              <h2 className={`text-xl font-bold ${t.text}`}>Comparar Período</h2>
              <p className={`text-sm ${t.textMut}`}>Selecione o período para comparar com o objetivo</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className={`block text-sm font-medium ${t.text} mb-2`}>Período de Comparação</label>
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={comparisonStart}
                    onChange={(e) => setComparisonStart(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                  />
                  <span className={t.textMut}>até</span>
                  <input
                    type="date"
                    value={comparisonEnd}
                    onChange={(e) => setComparisonEnd(e.target.value)}
                    className={`flex-1 px-4 py-2 rounded-lg ${t.inputBg} ${t.card} ${t.text}`}
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-zinc-700 flex justify-end gap-3">
              <Button
                onClick={() => { setShowCompareModal(false); setSelectedObjective(null); }}
                variant="outline"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCompare}
                disabled={!comparisonStart || !comparisonEnd}
                className="bg-violet-500 hover:bg-violet-600"
              >
                Comparar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Objective Modal */}
      {showEditModal && selectedObjective && (
        <EditObjectiveModal
          objective={selectedObjective}
          t={t}
          onClose={() => { setShowEditModal(false); setSelectedObjective(null); }}
          onSave={async (updatedTargets) => {
            try {
              await api.put(`/cutting-simulator/objectives/${selectedObjective.id}`, {
                targets: updatedTargets
              });
              toast.success('Objetivo atualizado');
              setShowEditModal(false);
              setSelectedObjective(null);
              fetchObjectives();
            } catch (error) {
              toast.error('Erro ao atualizar objetivo');
            }
          }}
          formatNumber={formatNumber}
          formatHoursAsHHMM={formatHoursAsHHMM}
        />
      )}
    </div>
  );
}

export default ObjectivesManagement;